import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import logger from '../utils/logger';
import TableSkeleton from './ui/TableSkeleton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminAssessmentResults = () => {
  const navigate = useNavigate();
  
  // State
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [selectedIndustry, setSelectedIndustry] = useState('all');
  const [selectedType, setSelectedType] = useState('all');
  const [showTailorModal, setShowTailorModal] = useState(false);
  const [selectedAssessment, setSelectedAssessment] = useState(null);
  const [selectedLogo, setSelectedLogo] = useState('rely');
  const [generatingReport, setGeneratingReport] = useState(false);
  const tailorModalRef = useRef(null);

  // Fetch all completed assessments
  useEffect(() => {
    fetchAssessments();
  }, []);

  const fetchAssessments = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${BACKEND_URL}/api/admin/completed-assessments`,
        { 
          withCredentials: true,
          timeout: 15000 // Standard timeout - increased from 8000ms for better reliability
        }
      );
      
      if (response.data.success) {
        setAssessments(response.data.assessments || []);
      }
    } catch (error) {
      // Handle timeout and network errors gracefully
      const isTimeout = error.code === 'ECONNABORTED' || error.message?.includes('timeout');
      const isContentLengthMismatch = error.message?.includes('ERR_CONTENT_LENGTH_MISMATCH');
      
      if (isTimeout || isContentLengthMismatch) {
        logger.debug(`Admin assessments fetch timeout/mismatch: ${error.message}`);
        toast.error('Request timed out. Please try refreshing the page.');
      } else {
        logger.error('Error fetching assessments:', error);
        toast.error('Failed to load assessments');
      }
      setAssessments([]); // Set empty array on error to show empty state
    } finally {
      setLoading(false);
    }
  };

  // Get unique industries for filtering
  const industries = ['all', ...new Set(assessments.map(a => a.industry_name).filter(Boolean))];
  
  // Filter assessments
  const filteredAssessments = assessments.filter(assessment => {
    const industryMatch = selectedIndustry === 'all' || assessment.industry_name === selectedIndustry;
    const typeMatch = selectedType === 'all' || assessment.assessment_type === selectedType;
    return industryMatch && typeMatch;
  });

  // Group by industry
  const groupedByIndustry = filteredAssessments.reduce((acc, assessment) => {
    const industry = assessment.industry_name || 'Unknown Industry';
    if (!acc[industry]) {
      acc[industry] = [];
    }
    acc[industry].push(assessment);
    return acc;
  }, {});

  // Pagination
  const totalPages = Math.ceil(Object.keys(groupedByIndustry).length / itemsPerPage);
  const paginatedIndustries = Object.keys(groupedByIndustry).slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Handle view result
  const handleViewResult = (assessment) => {
    if (assessment.assessment_type === 'ESA') {
      navigate(`/assessment/results/${assessment.id}`);
    } else if (assessment.assessment_type === 'EMHA') {
      navigate(`/emha/results/${assessment.id}`);
    }
  };

  // Handle download report using unified endpoint
// Handle download report using unified endpoint
const handleDownloadReport = async (assessment) => {
  try {
    toast.loading('Generating report...', { id: 'download' });
    
    // Determine report type
    const reportType = assessment.assessment_type === 'ESA' ? 'esa_basic' : 'emha_basic';
    
    logger.log('📄 Requesting report:', {
      assessment_id: assessment.id,
      report_type: reportType,
      assessment_type: assessment.assessment_type
    });
    
    const response = await axios.post(
      `${BACKEND_URL}/api/reports/generate-pdf`,
      { 
        assessment_id: assessment.id,
        report_type: reportType,
        branding: 'rely',
        check_payment: false
      },
      { 
        withCredentials: true,
        responseType: 'blob',
        headers: {
          'Accept': 'application/pdf'
        }
      }
    );

    logger.log('📄 Response received:', {
      status: response.status,
      contentType: response.headers['content-type'],
      size: response.data.size
    });

    // ✅ Check response is valid
    if (!response.data || response.data.size === 0) {
      throw new Error('Empty PDF response received');
    }

    // ✅ Check if it's actually a PDF
    const blob = response.data;
    const reader = new FileReader();
    
    reader.onloadend = function() {
      const arr = new Uint8Array(reader.result).subarray(0, 5);
      const header = String.fromCharCode(...arr);
      
      if (header !== '%PDF-') {
        logger.error('❌ Invalid PDF header:', header);
        toast.error('Received invalid PDF file', { id: 'download' });
        return;
      }
      
      // ✅ Valid PDF - proceed with download
      const url = window.URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${assessment.assessment_type}_Report_${assessment.facility_name.replace(/[^a-z0-9]/gi, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      setTimeout(() => {
        link.remove();
        window.URL.revokeObjectURL(url);
      }, 100);

      toast.success('Report downloaded successfully!', { id: 'download' });
    };
    
    reader.readAsArrayBuffer(blob);
    
  } catch (error) {
    logger.error('❌ Error downloading report:', error);
    const errorMsg = error.response?.data?.detail || error.message || 'Failed to download report';
    toast.error(errorMsg, { id: 'download' });
  }
};


  // Handle tailor report
  const handleTailorReport = (assessment) => {
    setSelectedAssessment(assessment);
    setShowTailorModal(true);
  };

  const handleGenerateTailoredReport = async () => {
    if (!selectedAssessment) return;

    try {
      setGeneratingReport(true);
      toast.loading('Generating tailored report...', { id: 'tailor' });

      // Determine report type based on assessment type
      const reportType = selectedAssessment.assessment_type === 'ESA' ? 'esa_basic' : 'emha_basic';

      const response = await axios.post(
        `${BACKEND_URL}/api/reports/generate-pdf`,
        {
          assessment_id: selectedAssessment.id,
          report_type: reportType,
          branding: selectedLogo,
          check_payment: false
        },
        {
          withCredentials: true,
          responseType: 'blob'
        }
      );

      // Check if response is valid
      if (!response.data || response.data.size === 0) {
        throw new Error('Empty PDF response received');
      }

      // Download with explicit PDF MIME type
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${selectedAssessment.assessment_type}_${selectedLogo}_Report.pdf`);
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      setTimeout(() => {
        link.remove();
        window.URL.revokeObjectURL(url);
      }, 100);

      toast.success('Tailored report downloaded!', { id: 'tailor' });
      setShowTailorModal(false);
    } catch (error) {
      logger.error('Error generating tailored report:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to generate tailored report';
      toast.error(errorMsg, { id: 'tailor' });
    } finally {
      setGeneratingReport(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
        <div className="max-w-7xl mx-auto px-4">
          {/* Header Skeleton */}
          <div className="mb-8">
            <div className="h-8 bg-gray-200 dark:bg-gray-600 rounded w-64 mb-2 animate-pulse" />
            <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded w-96 animate-pulse" />
          </div>

          {/* Filters Skeleton */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i}>
                  <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded w-32 mb-2 animate-pulse" />
                  <div className="h-10 bg-gray-200 dark:bg-gray-600 rounded w-full animate-pulse" />
                </div>
              ))}
            </div>
          </div>

          {/* Table Skeleton */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
            <div className="p-6">
              <TableSkeleton rows={5} cols={5} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            Assessment Results
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            View and manage all completed assessments
          </p>
        </div>

        {/* Filters */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Filter by Industry
              </label>
              <select
                value={selectedIndustry}
                onChange={(e) => {
                  setSelectedIndustry(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-emerald-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                {industries.map(industry => {
                  // For 'all' option, show as is
                  if (industry === 'all') {
                    return (
                      <option key="all" value="all">
                        All Industries
                      </option>
                    );
                  }
                  // For industry names from assessments, show as is (no region info available)
                  return (
                    <option key={industry} value={industry}>
                      {industry}
                    </option>
                  );
                })}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Filter by Type
              </label>
              <select
                value={selectedType}
                onChange={(e) => {
                  setSelectedType(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-emerald-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="all">All Types</option>
                <option value="ESA">ESA</option>
                <option value="EMHA">EMHA</option>
              </select>
            </div>

            <div className="flex items-end">
              <div className="text-sm text-gray-600 dark:text-gray-400">
                Total: <span className="font-semibold text-gray-900 dark:text-white">{filteredAssessments.length}</span> assessments
              </div>
            </div>
          </div>
        </div>

        {/* Assessments Grouped by Industry */}
        {paginatedIndustries.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-8 text-center">
            <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <p className="text-gray-600 dark:text-gray-400">No assessments found</p>
          </div>
        ) : (
          <>
            {paginatedIndustries.map(industry => (
              <div key={industry} className="mb-6">
                <div className="bg-emerald-50 dark:bg-emerald-900/20 border-l-4 border-emerald-500 px-4 py-2 mb-3">
                  <h2 className="text-lg font-semibold text-emerald-900 dark:text-emerald-300">
                    {industry} ({groupedByIndustry[industry].length})
                  </h2>
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          User / Facility
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Type
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Completed
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {groupedByIndustry[industry].map(assessment => (
                        <tr key={assessment.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div>
                              <div className="text-sm font-medium text-gray-900 dark:text-white">
                                {assessment.user_name || assessment.facility_name}
                              </div>
                              <div className="text-sm text-gray-500 dark:text-gray-400">
                                {assessment.facility_name}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                              assessment.assessment_type === 'ESA'
                                ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
                                : 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300'
                            }`}>
                              {assessment.assessment_type}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
                              Completed
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                            {new Date(assessment.completion_date).toLocaleDateString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <div className="flex justify-end space-x-2">
                              <button
                                onClick={() => handleViewResult(assessment)}
                                className="text-emerald-600 hover:text-emerald-900 dark:text-emerald-400 dark:hover:text-emerald-300"
                                title="View Result"
                              >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                </svg>
                              </button>
                              
                              <button
                                onClick={() => handleDownloadReport(assessment)}
                                className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                                title="Download Report"
                              >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                              </button>

                              <button
                                onClick={() => handleTailorReport(assessment)}
                                className="text-purple-600 hover:text-purple-900 dark:text-purple-400 dark:hover:text-purple-300"
                                title="Tailor Report"
                              >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
                                </svg>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between bg-white dark:bg-gray-800 rounded-lg shadow px-6 py-4">
                <div className="text-sm text-gray-700 dark:text-gray-300">
                  Page {currentPage} of {totalPages}
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Tailor Report Modal */}
      {showTailorModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={(e) => {
            // Close modal when clicking outside (on backdrop)
            if (e.target === e.currentTarget && !generatingReport) {
              setShowTailorModal(false);
              setSelectedAssessment(null);
            }
          }}
        >
          <div 
            ref={tailorModalRef}
            className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Tailor Report
              </h3>
            </div>

            <div className="p-6">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Select branding for the report:
              </p>

              <div className="space-y-3">
                <label className="flex items-center p-4 border-2 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                  <input
                    type="radio"
                    name="logo"
                    value="rely"
                    checked={selectedLogo === 'rely'}
                    onChange={(e) => setSelectedLogo(e.target.value)}
                    className="w-4 h-4 text-emerald-600 focus:ring-emerald-500"
                  />
                  <span className="ml-3 text-sm font-medium text-gray-900 dark:text-white">
                    Rely Ability Logo
                  </span>
                </label>

                <label className="flex items-center p-4 border-2 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                  <input
                    type="radio"
                    name="logo"
                    value="zurich"
                    checked={selectedLogo === 'zurich'}
                    onChange={(e) => setSelectedLogo(e.target.value)}
                    className="w-4 h-4 text-emerald-600 focus:ring-emerald-500"
                  />
                  <span className="ml-3 text-sm font-medium text-gray-900 dark:text-white">
                    Zurich Logo
                  </span>
                </label>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-3">
              <button
                onClick={() => setShowTailorModal(false)}
                disabled={generatingReport}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleGenerateTailoredReport}
                disabled={generatingReport}
                className="px-6 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
              >
                {generatingReport ? 'Generating...' : 'Generate Report'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toaster removed - using global Toaster from App.js */}
    </div>
  );
};

export default AdminAssessmentResults;
